import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormStateReturn
} from 'react-hook-form'
import {
  calcEmptyValue,
  getMenuItemStyle,
  handleChange,
  parseSelectedValueToLabels,
  renderValue
} from './helper'
import MenuItemValue from './menu-item-value'
import { SelectFieldProps } from './select-field'

type InputProps<TField extends FieldValues, TValue> = SelectFieldProps<TField, TValue> & {
  field: ControllerRenderProps<TField, Path<TField>>
  fieldState: ControllerFieldState
  formState: UseFormStateReturn<TField>
}

export default function Input<TField extends FieldValues, TValue>({
  name,
  required,
  disabled,
  label,
  options,
  size = 'small',
  variant,
  displayEmpty,
  multiple,
  emptyLabel = multiple ? 'Selecione uma ou mais opções' : 'Selecione uma opção',
  helperText,
  field,
  fieldState: { error },
  formState: { defaultValues },
  ...props
}: InputProps<TField, TValue>) {
  const labelId = `${name}-label`
  const finalLabel = required || disabled ? label : `${label} (opcional)`
  const defaultValue = defaultValues?.[name]
  const emptyValue = multiple ? '' : calcEmptyValue(options, defaultValue as string)

  const [selected, setSelected] = useState<string[]>(
    multiple ? parseSelectedValueToLabels(options, defaultValue ?? ['']) : []
  )

  const multipleUpdate = multiple
    ? () => setSelected(parseSelectedValueToLabels(options, field.value ?? ['']))
    : () => {}
  useEffect(multipleUpdate, [field.value])

  return (
    <FormControl fullWidth error={!!error}>
      <InputLabel id={labelId} size={size} shrink={true}>
        {finalLabel}
      </InputLabel>
      <Select
        id={`${name}-select`}
        labelId={`${labelId}`}
        variant={variant ? variant : 'outlined'}
        size={size}
        onChange={event =>
          handleChange(event as SelectChangeEvent<typeof selected>, field.onChange, multiple)
        }
        value={field.value ?? (multiple ? [''] : '')}
        renderValue={renderValue(multiple, options, emptyLabel)}
        multiple={multiple}
        label={name}
        disabled={disabled}
        displayEmpty={displayEmpty}
        input={<OutlinedInput label={finalLabel} name={name} />}
        slotProps={{
          input: {
            'aria-placeholder': `Selecione ${label}`,
            'aria-describedby': `${labelId}-helper-text`
          }
        }}
        MenuProps={{
          disableAutoFocusItem: multiple,
          disableScrollLock: true
        }}
        {...props}
      >
        {displayEmpty && (
          <MenuItem key="option-invalid" value={emptyValue} className="pointer-events-none">
            <em>{emptyLabel}</em>
          </MenuItem>
        )}
        {options?.map(option => (
          <MenuItem
            id={`${labelId}-helper-text`}
            key={option.value as string}
            value={(multiple ? `${option.value}` : option.value) as string}
            disabled={option.disabled}
            sx={getMenuItemStyle(multiple)}
          >
            <MenuItemValue label={option.label} multiple={multiple} selected={selected} />
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error.message || 'Selecione uma opção'}</FormHelperText>}
      {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}
